import React, {
  forwardRef,
  ForwardRefRenderFunction,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { Button, Loader, Modal } from 'ncoded-component-library';
import api from 'api';
import DownloadIcon from 'icons/Download.icon';
import ArrowleftIcon from 'icons/Arrowleft.icon';
import ArrowrightIcon from 'icons/Arrowright.icon';
import { PdfViewer } from 'components/PdfViewer';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import { useTranslation } from 'react-i18next';
import {
  extractNodeWatermarkSettings,
  getIsOwner,
  getUserFullName,
  isArrowLeftPressed,
  isArrowRightPressed,
  isDwg,
  isEml,
  isImage,
  isOfficeFileType,
  isPdf,
  isVideo,
} from 'utils';
import useCloseOnEscape from 'hooks/useCloseOnEscape';
import useFilePreviewTrackTime from 'hooks/useFilePreviewTrackTime';
import useDisableRightClick from 'hooks/useDisableRightClick';
import { SoftSignal } from 'models/Lead';
import SoftSignals from './components/SoftSignals';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { BUYING_PORTFOLIO_ROUTE } from 'data/global';
import { useNavigate } from 'react-router-dom';
import useMixpanelDocumentEvents from 'hooks/useMixpanelDocumentEvents';
import OfficeFilePreview from 'components/OfficeFilePreview';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { DataRoomFileBufferPreview } from 'models/File';
import useStartDownloadingFile from 'hooks/useStartDownloadingFile';
import useFileBufferPreviewQuery from 'hooks/useFileBufferPreviewQuery';
import useFileContentQuery from 'hooks/useFileContentQuery';
import useObjectUrl from 'hooks/useObjectUrl';
import CannotPreviewInBrowser from 'components/CannotPreviewInBrowser';
import AutodeskViewer from 'components/AutodeskViewer';
import { QAndASides } from 'router/subrouters/DataRoom/models/QAndA';
import { NeedToSignNdaModalRef } from 'components/NeedToSignNdaModal/NeedToSignNdaModal.component';
import NeedToSignNdaModal from 'components/NeedToSignNdaModal';
import ShareableLinkNdaProcessing from 'router/subrouters/ShareableLink/components/ShareableLinkNdaProcessing';
import ShareableLinkNdaCountersign from 'router/subrouters/ShareableLink/components/ShareableLinkNdaCountersign';
import useKeyDown from 'hooks/useKeyDown';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import mixpanelService from 'services/mixpanelService';
import { SoftSignalSeenEventProps } from 'models/MixpanelEvents';

import '@react-pdf-viewer/core/lib/styles/index.css';
import './DataroomNodeViewModal.styles.scss';
import './DataroomNodeViewModal.styles.responsive.scss';

const getCannotPreviewInBrowser = ({
  type,
  urn,
  isAutodeskProccessed,
}: DataRoomFileBufferPreview) =>
  isEml(type) || (isDwg(type) && (!urn || !isAutodeskProccessed));

export type SoftSignalsDetails = {
  displayBanner?: boolean;
  lastSoftSignal?: SoftSignal;
};

type DataroomNodeViewModalProps = {
  className?: string;
  node: DataRoomNode;
  isCurrentlyActiveNode?: boolean;
  dataRoomName?: string;
  ownerId: string;
  userSide: QAndASides;
  dealRoomId: string;
  onPrev?: () => void;
  onNext?: () => void;
};

const DataroomNodeViewModal: ForwardRefRenderFunction<
  ModalRef,
  DataroomNodeViewModalProps
> = (props, ref) => {
  const {
    className,
    node,
    dataRoomName,
    ownerId,
    userSide,
    dealRoomId,
    isCurrentlyActiveNode = true,
    onPrev,
    onNext,
  } = props;

  const {
    _id: nodeId,
    name: documentName,
    dataRoomId,
    canDownload,
    version,
    isBuyerFile,
    userId,
  } = node || {};

  const { t } = useTranslation();

  const classes = classNames('data-room-node-view-modal', className);

  const [Fallback, setFallback] = useState<ReactNode>();

  const {
    currentUser: { _id: currentUserId, firstName, lastName, email },
  } = useContext(CurrentUserContext);
  const { popServerError, popSuccess } = useContext(PopNotificationsContext);

  const navigate = useNavigate();

  const [modal, modalRef] = useCallbackRef<ModalRef>();
  const [needToSignNdaModal, needToSignNdaModalRef] =
    useCallbackRef<NeedToSignNdaModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const isModalOpen = modal?.openState;

  const { query, removeFromCache, updateSoftSignal } =
    useFileBufferPreviewQuery({
      nodeId,
      isCurrentlyActiveNode,
      version,
      enabled: !!isModalOpen && !!nodeId && !Fallback,
    });

  const { data, isFetching } = query;

  const { url, type, autodeskToken, urn, displayBanner, lastSoftSignal } =
    data || {};

  // cache the actual file bytes for browser-rendered types so re-opening the same
  // file doesn't re-download it; office (MS viewer) and dwg (Autodesk) need the
  // signed url / urn and cannot be byte-cached client-side.
  const isBrowserRenderable = isPdf(type) || isImage(type) || isVideo(type);

  const {
    data: fileBlob,
    isFetching: isContentFetching,
    isError: isContentError,
  } = useFileContentQuery({
    nodeId,
    url,
    version,
    enabled: !!isModalOpen && isBrowserRenderable && !!url && !Fallback,
  });

  const objectUrl = useObjectUrl(fileBlob);

  // fall back to the signed url if byte caching fails (e.g. CORS) so the preview
  // still renders instead of showing nothing.
  const previewUrl = isBrowserRenderable
    ? objectUrl ?? (isContentError ? url : undefined)
    : url;
  const isPreviewFetching =
    isFetching ||
    (isBrowserRenderable &&
      !objectUrl &&
      !isContentError &&
      (isContentFetching || !!fileBlob));

  const userFullName = useMemo(
    () => getUserFullName(firstName, lastName),
    [firstName, lastName],
  );

  useDisableRightClick(isModalOpen);

  const startDownloadingFile = useStartDownloadingFile();

  const RenderTitle = useMemo(
    () =>
      canDownload && (
        <Button
          variant="outline"
          className="data-room-node-view-modal__download"
          icon={DownloadIcon}
          onClick={() => startDownloadingFile(nodeId)}
        >
          {t('download')}
        </Button>
      ),
    [canDownload, nodeId, startDownloadingFile, t],
  );

  // react to the fetched preview: NDA / processing states are evicted from cache so
  // they always re-check the server, while previewable / cannot-preview responses stay
  // cached (handled by the query hook).
  useEffect(() => {
    if (!isModalOpen || !query.isSuccess) return;

    if (!data) {
      removeFromCache();
      modal.close();
      return;
    }

    const {
      requiresNdaSign,
      shareableLinkId,
      isWaitingNdaConfirmation,
      isWaitingCounterSign,
    } = data;

    if (isWaitingNdaConfirmation) {
      removeFromCache();
      setFallback(<ShareableLinkNdaProcessing />);
      return;
    }

    if (isWaitingCounterSign) {
      removeFromCache();
      setFallback(<ShareableLinkNdaCountersign />);
      return;
    }

    if (requiresNdaSign) {
      removeFromCache();
      modal.close();
      needToSignNdaModal.open(
        api.shareableLinks.getNDADocusignUrl(shareableLinkId, {
          redirectUrl: window.location.href,
        }),
      );
      return;
    }

    if (getCannotPreviewInBrowser(data)) {
      // a DWG here is still being processed by Autodesk (no urn yet / not processed) —
      // that's a transient state, so don't cache it. Otherwise re-opening keeps
      // serving the stale "cannot preview" and re-downloads instead of rendering in
      // the viewer once processing finishes.
      if (isDwg(data.type)) removeFromCache();

      startDownloadingFile(nodeId);
      setFallback(<CannotPreviewInBrowser />);
    }
  }, [
    isModalOpen,
    query.isSuccess,
    data,
    nodeId,
    removeFromCache,
    startDownloadingFile,
    modal,
    needToSignNdaModal,
  ]);

  useEffect(() => {
    if (!query.isError) return;

    popServerError(query.error);
    modal?.close();
  }, [query.isError, query.error, modal, popServerError]);

  const sendReport = useCallback(
    (time: number, currentPage: number) => {
      api.dataRoom.reports.trackTime(
        dataRoomId,
        nodeId,
        currentPage,
        time,
        version || 1,
      );
    },
    [dataRoomId, nodeId, version],
  );

  const getMixpanelProps = useGetMixpanelProps();

  const { onDocumentViewed } = useMixpanelDocumentEvents({
    documentName,
    dataRoomName,
    ownerId,
    userSide,
  });

  const { isLoaded, onDocumentLoad, onPageChange } = useFilePreviewTrackTime({
    isModalOpen,
    trackingKey: nodeId,
    sendReport,
    onDocumentViewed,
  });

  const onSoftSignalSuccess = useCallback(
    (lastSoftSignal: SoftSignal, withdrawInterest?: boolean) => {
      updateSoftSignal(lastSoftSignal);

      if (withdrawInterest) {
        popSuccess(t('interestWithdrawnSuccessfully'));
        modal?.close();
        setTimeout(() => navigate(BUYING_PORTFOLIO_ROUTE), 2000);
      }
    },
    [modal, navigate, popSuccess, t, updateSoftSignal],
  );

  const renderFooter = useMemo(
    () =>
      isLoaded &&
      displayBanner &&
      !isBuyerFile &&
      !getIsOwner(userId, currentUserId),
    [displayBanner, isBuyerFile, isLoaded, userId, currentUserId],
  );

  useEffect(() => {
    if (!isModalOpen || !renderFooter || lastSoftSignal) return;

    mixpanelService.softSignalSeen(
      getMixpanelProps({
        dataRoomId,
        documentId: nodeId,
        dealId: dealRoomId,
      }) as SoftSignalSeenEventProps,
    );
  }, [
    isModalOpen,
    renderFooter,
    dataRoomId,
    nodeId,
    dealRoomId,
    lastSoftSignal,
    getMixpanelProps,
  ]);

  const RenderFooter = useMemo(
    () =>
      renderFooter && (
        <SoftSignals
          lastSoftSignal={lastSoftSignal}
          dataRoomNodeId={nodeId}
          dataRoomId={dataRoomId}
          dealRoomId={dealRoomId}
          onSuccess={onSoftSignalSuccess}
        />
      ),
    [
      renderFooter,
      lastSoftSignal,
      dataRoomId,
      dealRoomId,
      nodeId,
      onSoftSignalSuccess,
    ],
  );

  const canNavigate = useMemo(() => !!onPrev || !!onNext, [onNext, onPrev]);

  // on close: reset the fallback, and for office/dwg drop the cached preview.
  // those hand a short-lived signed url/token to an external viewer that downloads
  // the file server-side, so a cached (expired) url breaks the next open with
  // "Download failed" — re-opening must fetch a fresh url. Browser-renderable types
  // stay cached (their bytes are byte-cached separately, so the url is never reused).
  useEffect(() => {
    if (isModalOpen) return;

    if (Fallback) setFallback(null);

    if (data && (isOfficeFileType(type) || isDwg(type))) removeFromCache();
  }, [isModalOpen, Fallback, data, type, removeFromCache]);

  useCloseOnEscape(modal?.close, { capture: true, enabled: isModalOpen });

  // navigating to a sibling file changes nodeId; clear any fallback from the
  // previous file (cannot-preview / NDA / processing) so it doesn't carry over
  // onto the next one. The preview queries are keyed by nodeId and refetch on
  // their own.
  useEffect(() => {
    setFallback(null);
  }, [nodeId]);

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (isArrowLeftPressed(e)) onPrev?.();
      if (isArrowRightPressed(e)) onNext?.();
    },
    [onPrev, onNext],
  );

  useKeyDown({ onKeyDown, enabled: isModalOpen && canNavigate });

  return (
    <>
      <Modal
        ref={modalRef}
        className={classes}
        title={RenderTitle}
        footer={RenderFooter}
        lockScroll={false}
      >
        {isPreviewFetching && <Loader />}

        {Fallback}

        {canNavigate && (
          <>
            <Button
              type="button"
              variant="icon"
              icon={ArrowleftIcon}
              className="data-room-node-view-modal__nav prev"
              onClick={onPrev}
            />
            <Button
              type="button"
              variant="icon"
              icon={ArrowrightIcon}
              className="data-room-node-view-modal__nav next"
              onClick={onNext}
            />
          </>
        )}

        {isPdf(type) && (
          <PdfViewer
            url={previewUrl}
            onPageChange={onPageChange}
            onDocumentLoad={onDocumentLoad}
          />
        )}

        {isImage(type) && previewUrl && (
          <img
            alt={documentName}
            src={previewUrl}
            onLoad={() => onDocumentLoad()}
          />
        )}

        {isVideo(type) && (
          <video
            src={previewUrl}
            controls
            controlsList="nodownload"
            onLoadedData={() => onDocumentLoad()}
          />
        )}

        {isOfficeFileType(type) && (
          <OfficeFilePreview
            data={data}
            email={email}
            fullName={userFullName}
            watermarkSettings={extractNodeWatermarkSettings(node)}
            onDocumentLoad={onDocumentLoad}
          />
        )}

        {isDwg(type) && (
          <AutodeskViewer
            urn={urn}
            accessToken={autodeskToken}
            email={email}
            fullName={userFullName}
            ipAddress={data?.ipAddress}
            watermarkSettings={extractNodeWatermarkSettings(node)}
            onDocumentLoad={onDocumentLoad}
          />
        )}
      </Modal>

      <NeedToSignNdaModal
        ref={needToSignNdaModalRef}
        title={t('signNdaToAccessThisFile')}
      >
        <p>{t('thisFileRequiresSignedNDABeforeAccessIsGranted')}</p>
      </NeedToSignNdaModal>
    </>
  );
};

export default forwardRef(DataroomNodeViewModal);
